import { IonCheckbox } from '@ionic/react'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { COUNTRY } from '../../const/constants'
import { useAppContext } from '../../context/AppContext'
import { useStepsContext } from '../../context/StepsContext'
import useRequests from '../../hooks/useRequests'
import { saveClientSign } from '../../redux/actions'
import Button from '../Button'
import SignatureArea from './SignatureArea'

interface Props {
  procedure: 'activationPP' | 'portabilidad' | 'nominativity'
}

const Signature = ({ procedure }: Props) => {
  const { settings }: any = useAppContext()
  const { newProcedureStep, setNewProcedureStep, setSuccess }: any = useStepsContext()
  const { signDocumentAndActivate, processPreactivation } = useRequests('idPrepagaForm')
  const { signDocumentPortability } = useRequests('lineValidationFormPY')
  const [sign, setSign] = useState({
    base64img: '',
    validSize: false,
    isEmpty: true,
    isPoint: undefined,
    isLine: undefined
  })
  const [alreadySigned, setAlreadySigned] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  const dispatch = useDispatch()

  const tyc = settings.signature.urls.termsAndConditions
  const policies = settings.signature.urls.policies
  const regulation = settings.signature.urls.clientRegulation

  const finishProcedure = async () => {
    let response

    if (procedure === 'activationPP') response = await signDocumentAndActivate(sign.base64img)
    else if (procedure === 'portabilidad') response = await signDocumentPortability(sign.base64img)
    else response = await processPreactivation(sign.base64img.split(',')[1])

    return response
  }

  const onContinue = async () => {
    dispatch(saveClientSign(sign.base64img))
    const serviceResponse = await finishProcedure()

    if (serviceResponse?.success) setSuccess(true)
    setNewProcedureStep(newProcedureStep + 1)
  }

  const signChanged = (image: any) => {
    image.img && setAlreadySigned(true)
    const minPerc = settings.signature.minPercentage
    const enableVerification = settings.signature.enableValidation
    setSign({
      base64img: image.img,
      validSize: enableVerification ? image.percX > minPerc || image.percY > minPerc : true,
      isEmpty: !image.img,
      isPoint: image.allPoints,
      isLine: image.isLine
    })
  }

  const validity: boolean =
    !sign.base64img || !sign.validSize || !acceptedTerms || sign.isEmpty || !!sign.isLine || !!sign.isPoint

  const portaConditions = (
    <>
      <span>, las </span>
      <a id="signature-policies" className="signature-terms-link" href={policies} target="_blank" rel="noreferrer">
        Políticas Aplicables
      </a>
      <span> y declaro conocer el </span>
      <a id="signature-regulation" className="signature-terms-link" href={regulation} target="_blank" rel="noreferrer">
        Reglamento de Clientes
      </a>
    </>
  )

  const nomConditions = (
    <>
      <span>, la Solicitud de Servicio y las </span>
      <a id="signature-policies" className="signature-terms-link" href={policies} target="_blank" rel="noreferrer">
        Políticas Aplicables
      </a>
      <span> al Servicio</span>
    </>
  )

  const errorMsg = () => {
    if (sign.isEmpty && !acceptedTerms) return 'Faltan campos obligatorios.'
    else if (sign.isEmpty) return 'Es necesario firmar para poder continuar.'
    else if (sign.isPoint) return 'La firma no puede ser un punto.'
    else if (sign.isLine) return 'La firma debe ser más grande.'
    else if (!sign.validSize) return 'La firma debe ser más grande.'
    else if (!acceptedTerms) return 'Es necesario aceptar para poder continuar.'
    return ''
  }

  const renderConditions = () => {
    if (settings.country === COUNTRY.AR && procedure === 'nominativity') return nomConditions
    else if (settings.country === COUNTRY.AR && procedure === 'portabilidad') return portaConditions
    else return ''
  }

  const signature = (
    <div className="signature">
      <div className="signature-container">
        <div className="signature-title">Firma del cliente</div>

        <SignatureArea onChange={signChanged} />

        <div className="signature-errors">
          {alreadySigned && <span className="signature-errors-messages">{errorMsg()}</span>}
        </div>

        <div className="signature-terms">
          <div className="signature-terms-check">
            <IonCheckbox
              id="signature-checkbox-tyc"
              value={acceptedTerms}
              onClick={(e) => setAcceptedTerms(!acceptedTerms)}
            ></IonCheckbox>
          </div>
          <div className="signature-terms-text">
            He leído y acepto los{' '}
            <a
              id="signature-terms-conditions"
              className="signature-terms-link"
              href={tyc}
              target="_blank"
              rel="noreferrer"
            >
              Términos y Condiciones
            </a>
            {renderConditions()}.
          </div>
        </div>
        <div className="signature-action">
          <Button
            id="sign-terms-conditions"
            handleSubmit={onContinue}
            className="submit-button"
            label="Continuar"
            validity={validity}
          />
        </div>
      </div>
    </div>
  )

  return <>{signature}</>
}

export default Signature
